<script setup lang="ts">
    import { type FormKitNode } from '@formkit/core';
    import Skeleton from '@shared/components/ui/skeleton.vue';
    import { FormSizeTypeEnum, FormStyleEnum } from '@shared/constants/form-style';
    import { useWindowSize } from '@vueuse/core';

    const props = defineProps<{
        modelValue?: string
        name: string
        id?: string
        label?: string
        placeholder?: string
        validation?: string | [rule: string, ...args: unknown[]][]
        validationVisibility?: 'submit' | 'live' | 'blur' | 'dirty' | undefined,
        help?: string
        disabled?: boolean
        style?: FormStyleEnum.PRIMARY | FormStyleEnum.SECONDARY
        size?: FormSizeTypeEnum.NORMAL | FormSizeTypeEnum.EXTRA
        labelHidden?: boolean
        isLoading?: boolean
        autoHeight?: boolean
        ignore?: boolean
    }>();

    // eslint-disable-next-line func-call-spacing
    const emit = defineEmits<{
        (event: 'update:modelValue', value: string): void
        (event: 'change' | 'blur'): void
    }>();

    const { width } = useWindowSize();
    const { isDesktop, isMobile } = useAppBreakpoints();

    const valueModel: Ref<string> = ref(props.modelValue ?? '');
    const textareaWrapper:Ref<HTMLDivElement | null> = ref(null);
    const isMounted: Ref<boolean> = ref(false);
    const delayedHeightCalculationTimer: Ref<ReturnType<typeof setTimeout> | null> = ref(null);
    const formKitNode: Ref<FormKitNode | null> = ref(null);
    const visibilityObserver: Ref<ResizeObserver | null> = ref(null);
    const wasVisible: Ref<boolean> = ref(false);

    const skeletonHeight: ComputedRef<string> = computed(() => isMobile.value ? '160px' : isDesktop.value ? '192px' : '176px');

    // Apply calculated height to textarea element
    const applyHeight = (textarea: HTMLTextAreaElement): void => {
        // Skip when the field is not currently rendered/visible (e.g. inside a
        // collapsed accordion or a display:none container). Measuring then yields
        // scrollHeight 0 and would lock the textarea at height: 0px; min-height: 0px.
        if (textarea.offsetParent === null) return;

        // Store current scroll position to prevent jump
        const { scrollTop } = textarea;

        // Temporarily set overflow to hidden to prevent scrollbar flicker
        const originalOverflow = textarea.style.overflow;
        textarea.style.overflow = 'hidden';

        // Reset height to auto to get accurate scrollHeight measurement
        // Use a specific technique to avoid visual jump: set height to 0 first
        textarea.style.height = '0';

        // Get the scrollHeight (already includes padding)
        const { scrollHeight } = textarea;

        // Guard against a 0 measurement (still effectively hidden): restore the
        // styles and bail so the CSS min-block-size keeps the field sized.
        if (scrollHeight === 0) {
            textarea.style.height = '';
            textarea.style.overflow = originalOverflow;
            return;
        }

        // Set the new height directly (scrollHeight already includes padding)
        textarea.style.height = `${scrollHeight}px`;
        textarea.style.minHeight = `${scrollHeight}px`;

        // Restore overflow
        textarea.style.overflow = originalOverflow;

        // Restore scroll position
        textarea.scrollTop = scrollTop;
    };

    // Calculate and set textarea height based on content
    const calculateHeight = (): void => {
        if (!props.autoHeight) return;

        // Use requestAnimationFrame for better timing. Run twice so that values
        // injected into the DOM a tick later (e.g. when the field lives inside a
        // FormKit group/list) are measured once the content is actually present.
        requestAnimationFrame(() => {
            const textarea = textareaWrapper.value?.querySelector('textarea');
            if (!textarea) return;

            applyHeight(textarea);

            requestAnimationFrame(() => {
                applyHeight(textarea);
            });
        });
    };

    // Recalculate height whenever the FormKit node commits a value. This reliably
    // covers values injected by a parent FormKit group/list on initial render,
    // which the modelValue/valueModel watchers do not always observe.
    const handleNode = (node: FormKitNode): void => {
        formKitNode.value = node;
        node.on('commit', () => {
            if (props.autoHeight) {
                calculateHeight();
            }
        });
    };

    // Observe visibility so the height is (re)calculated the moment the field
    // becomes visible. This covers fields mounted inside a display:none container
    // (collapsed accordion / hidden time-slot description) where the initial
    // measurement would otherwise be 0. Width is tracked because applyHeight only
    // mutates height, so reacting to width transitions avoids feedback loops.
    const setupVisibilityObserver = (): void => {
        if (!props.autoHeight || !textareaWrapper.value || typeof ResizeObserver === 'undefined') {
            return;
        }

        visibilityObserver.value = new ResizeObserver(entries => {
            const isVisible = entries.some(entry => entry.contentRect.width > 0);
            if (isVisible && !wasVisible.value) {
                wasVisible.value = true;
                calculateHeight();
            } else if (!isVisible) {
                wasVisible.value = false;
            }
        });

        visibilityObserver.value.observe(textareaWrapper.value);
    };

    // Set mounted flag after component is ready
    onMounted(async() => {
        await nextTick(() => {
            isMounted.value = true;

            // Initial height calculation after mount
            if (props.autoHeight) {
                calculateHeight();

                // Also calculate after a longer delay to handle accordion cases
                delayedHeightCalculationTimer.value = setTimeout(() => {
                    calculateHeight();
                }, 300);

                setupVisibilityObserver();
            }
        });
    });

    // Clean up timer on component unmount
    onBeforeUnmount(() => {
        if (delayedHeightCalculationTimer.value) {
            clearTimeout(delayedHeightCalculationTimer.value);
            delayedHeightCalculationTimer.value = null;
        }

        if (visibilityObserver.value) {
            visibilityObserver.value.disconnect();
            visibilityObserver.value = null;
        }
    });

    // Sync prop modelValue with internal valueModel
    watch(() => props.modelValue, newValue => {
        if (newValue !== valueModel.value) {
            valueModel.value = newValue ?? '';
        }

        // Recalculate height when modelValue changes from parent
        if (props.autoHeight && isMounted.value) {
            calculateHeight();
        }
    });

    // Emit updated value when valueModel changes
    watch(valueModel, newValue => {
        // Only emit update:modelValue event after component is mounted to prevent triggering on initial load
        if (isMounted.value) {
            emit('update:modelValue', newValue);
        }

        // Recalculate height when user types
        if (props.autoHeight) {
            calculateHeight();
        }
    });

    // Watch for when autoHeight prop changes
    watch(() => props.autoHeight, newVal => {
        if (newVal && isMounted.value) {
            calculateHeight();
        }
    });

    // Recalculate height when window is resized
    watch(width, () => {
        if (props.autoHeight && isMounted.value) {
            calculateHeight();
        }
    });
</script>

<template>
    <div
        ref="textareaWrapper"
        class="textarea"
        data-constell="constell-textarea"
    >
        <Skeleton
            v-if="isLoading"
            :height="skeletonHeight"
        />
        <FormKit
            v-else
            :id="id ?? name ?? 'textarea'"
            v-model="valueModel"
            :label="label"
            :name="name"
            :placeholder="placeholder"
            :validation="validation"
            :validation-visibility="validationVisibility"
            :help="help"
            :inner-class="style ?? FormStyleEnum.PRIMARY"
            :wrapper-class="size ?? FormSizeTypeEnum.NORMAL"
            :disabled="disabled"
            :label-class="labelHidden ? 'formkit-label--hidden' : ''"
            :auto-height="autoHeight"
            :ignore="ignore"
            type="textarea"
            data-constell="constell-textarea-change"
            @node="handleNode"
            @change="$emit('change')"
            @blur="$emit('blur')"
        />
    </div>
</template>

<style lang="scss" scoped>
    .textarea :deep(.formkit-outer) {
        .formkit-inner {
            align-items: flex-start;
            border: 1px solid var(--border-tertiary);
            border-radius: var(--border-radius-xs);
            background-color: var(--background-form-input);
            min-block-size: 160px;

            &:hover,
            &:focus,
            &:focus-visible,
            &:focus-within {
                background-color: var(--background-form-input-active);
            }

            @include min-width(md) {
                min-block-size: 176px;
            }

            @include min-width(lg) {
                min-block-size: 185px;
            }

            textarea {
                box-sizing: border-box;
                padding: var(--spacing-03);
                border: none;
                border-radius: var(--border-radius-xs);
                background: none;
                block-size: 100%;
                font-family: var(--font-primary);
                font-size: var(--font-medium);
                font-weight: var(--font-weight-normal);
                inline-size: 100%;
                line-height: 1.2;
                resize: none;

                &::placeholder {
                    color: var(--text-placeholder);
                }
            }

            &.secondary {
                &:hover {
                    background-color: var(--background-form-input-hover);
                }

                &:focus,
                &:focus-visible,
                &:focus-within {
                    background-color: var(--background-form-input-active);
                }
            }
        }

        &[data-invalid=true] .formkit-inner {
            textarea,
            &.secondary textarea {
                border-color: var(--border-negative);
            }
        }

        &[data-required=true] {
            .formkit-label::after {
                color: var(--text-secondary);
                content:" *";
            }
        }

        &[data-disabled=true] .formkit-inner {
            border-color: var(--border-disabled-1);
            background-color: var(--background-disabled-1);

            &:hover,
            &:focus,
            &:focus-visible,
            &:focus-within {
                background-color: var(--background-disabled-2);
            }

            textarea,
            &.secondary textarea {
                color: var(--text-disabled-1);
                cursor: not-allowed;
            }
        }
    }
</style>
